// ============================================================
// AssistantEngine — der imperative Kern der AssistantBar (React-frei).
//
// Enthält 1:1 verschoben aus AssistantBar.js (Vanilla):
//   • Text-Chat (Verlauf, /chat-Fetch, ui_command-Dispatch)
//   • Verlaufs-Persistenz (save/list/open/delete/new)
//   • Formular-Flüsse (E-Mail-Versand, Kunden-Formular mit Voice-Kickoff)
//   • Headless-Voice-Steuerung (Mobile-Nav-Mic)
//   • Transkript-Puffer + Werkzeug-Spur der Sprachsitzung
//   • DEBUG-Spur (lu_voice_debug)
// Die eigentliche Sprachsitzung (Gemini Live, Audio) liegt in voice.ts,
// die Tool-Ausführung in tools.ts — beide arbeiten auf DIESER Instanz.
//
// DOM-Schreibzugriffe des alten Codes sind zu ZUSTAND geworden (messages,
// hint, micRecording, panelOpen, …); die React-Insel abonniert die Engine
// über subscribe()/getVersion() (useSyncExternalStore) und rendert.
// ============================================================
import { t, getLanguage } from '../../../i18n/index.js';
import { events, EVENTS } from '../../../core/events.js';
import { dispatchUiCommand, getOpenDocument, setOpenDocument, getSendSheetOpen } from '../../../core/uiCommands.ts';
// Gesprächsrunde: der Beleg dafür, dass zwischen Vorschlag und Zustimmung
// wirklich der Nutzer geredet hat (core/assistantTurn.ts).
import { getTurnKey, startTurn } from '../../../core/assistantTurn.ts';
// @ts-expect-error — core/voiceTargets.js ist reines JS ohne Typen
import { collectVoiceTargets } from '../../../core/voiceTargets.js';
import {
  authedFetch, errDetail,
  VOICE_CHAT_URL, VOICE_HISTORY_BASE, VOICE_EMAIL_URL, VOICE_RUNTOOL_URL,
} from './api.ts';
import { NAV_UI_COMMANDS } from './tools.ts';
import { startVoice, stopVoice, stopPlayback, tick } from './voice.ts';
import type {
  ChatMessage, ChatWho, CustomerPrefill, EngineHost, HeadlessState,
  HistEntry, HistoryChatMeta, MsgHandle, PanelView, TextMessage,
} from './types.ts';

// SiriWave-Instanz + Gemini-Live-Session sind bewusst untypisiert (any):
// beide kommen aus voice.ts und werden hier nur gehalten/geprüft.
/* eslint-disable @typescript-eslint/no-explicit-any */

export class AssistantEngine {
  // ── External-Store (React-Anbindung) ──────────────────────
  private listeners = new Set<() => void>();
  private version = 0;
  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => { this.listeners.delete(fn); };
  };
  getVersion = (): number => this.version;
  emit(): void {
    this.version += 1;
    this.listeners.forEach((fn) => { try { fn(); } catch { /* Listener-Fehler nie propagieren */ } });
  }

  // ── Imperative UI-Griffe (React registriert sich beim Mount) ──
  host: EngineHost | null = null;
  setHost(h: EngineHost | null): void { this.host = h; }

  // ── UI-Zustand (früher CSS-Klassen/DOM) ───────────────────
  messages: ChatMessage[] = [];
  private msgSeq = 0;
  // Zähler statt «bei jedem Render scrollen»: erhöht sich exakt dort, wo der
  // alte Code chat.scrollTop = chat.scrollHeight setzte.
  chatScrollTick = 0;
  panelOpen = false;
  panelView: PanelView = 'chat';
  histCollapsed = true; // pishfarz: sidebar baste
  hint: string | null = null; // null ⇒ t('assistant.listening')
  micRecording = false;
  micConnecting = false;
  visualsPaused = false; // ersetzt display:none auf .ab-offer-frame + rAF-Stopp
  allChats: HistoryChatMeta[] | null = null; // null ⇒ Ladeanzeige «...»
  emailForm: { offerId: string | null; visible: boolean; status: string; seq: number } | null = null;
  custForm: { prefill: CustomerPrefill; status: string; seq: number } | null = null;
  private formSeq = 0;

  // ── TEXT mode (vasl be Gemini poshte backend) ──
  chatHistory: HistEntry[] = [];
  lastOfferId: string | null = null;
  currentChatId: string | null = null;   // ID-e chat-e fa'al; ta hame rooye yek chat zakhire shavad
  currentChatKind: 'text' | 'voice' = 'text'; // Sprachsitzungen werden getrennt abgelegt
  awaitingEmail = false;
  pendingEmailOfferId: string | null = null;

  // ── VOICE mode — Zustand der Sprachsitzung (siehe voice.ts) ──
  audioCtx: AudioContext | null = null;
  analyser: AnalyserNode | null = null;
  micStream: MediaStream | null = null;
  rafId: number | null = null;
  dataArr: Uint8Array<ArrayBuffer> | null = null;
  siri: any = null;
  siriRetry = 0;
  session: any = null;
  // Generationszähler gegen das «schwarz, aber redet weiter»-Loch: stopVoice
  // erhöht ihn, startVoice merkt sich seinen Wert. Ein connect(), das erst NACH
  // dem Stopp fertig wird, sieht die fremde Generation und macht sich selbst
  // wieder zu — vorher blieb genau so ein Geister-Socket am Leben und playChunk
  // baute sich sogar einen frischen AudioContext, obwohl längst Schluss war.
  voiceGen = 0;
  voiceActive = false;
  playCtx: AudioContext | null = null;
  playTime = 0;
  playSources: AudioBufferSourceNode[] = [];
  playGain: GainNode | null = null;
  processor: AudioWorkletNode | null = null;
  micSource: MediaStreamAudioSourceNode | null = null;
  micPaused = false;
  formOpen = false;
  voiceOfferId: string | null = null; // offer_id-e akhar dar halat-e seda
  // ── «Auflegen» nach der Verabschiedung ──────────────────────
  // end_voice_session setzt das Flag, statt die Sitzung sofort zu kappen:
  // sonst würde dem Modell das «Tschüss» im Mund abgeschnitten. Beendet wird
  // erst, wenn der Satz fertig GESPROCHEN ist (turnComplete + Puffer leer).
  // Redet der Nutzer doch weiter, wird beides wieder zurückgenommen.
  voiceEndAfterTurn = false;
  voiceEndTimer: ReturnType<typeof setTimeout> | null = null;
  // ── Bildschirm-Spiegel der Sprachsitzung ────────────────────
  // Jede Sekunde wird der Bildschirm (offenes Dokument + tippbare Ziele) als
  // kompakter Text eingesammelt; NUR wenn er sich vom zuletzt GESENDETEN
  // unterscheidet, geht ein «SCREEN»-Kontextblock an die Live-Session
  // (turnComplete:false — reiner Kontext, keine Antwort-Auslösung). So weiss
  // das Modell vor jeder Antwort, was wirklich auf dem Bildschirm steht.
  screenTimer: ReturnType<typeof setInterval> | null = null;
  screenLastSent = '';

  // ── Gesprächsverlauf der Sprachsitzung ──────────────────────
  // Gemini schickt die Transkription in vielen kleinen Stücken. Pro Rolle
  // puffern und erst bei finished/turnComplete als EINE Nachricht ablegen —
  // sonst entsteht pro Silbe ein Eintrag.
  vtUserBuf = ''; vtModelBuf = '';
  vtUserLang = ''; vtModelLang = '';


  // ── Headless-Voice (Mobile-Nav-Mic): kein Panel, Zustand geht per Callback
  // an die Mobile-UI. Braucht ein Voice-Tool doch UI (Formular/Dokumentliste),
  // öffnet exitHeadlessForVisuals das Panel und meldet 'visual'.
  headless = false;
  onHeadlessState: ((s: HeadlessState) => void) | null = null;
  onHeadlessActivity: ((label: string | null) => void) | null = null;

  // ── DEBUG-Spur der Sprachsitzung ──
  // Sie gehört in die Konsole, NICHT unter den Orb: dort steht «Zuhören …» für
  // den Nutzer, und Zeilen wie «tool: create_offer» oder «audio in» zeigen ihm
  // die Maschinerie statt einer Antwort. Zum Mitlesen auf dem Gerät (iPhone, wo
  // es keine Konsole gibt): localStorage.setItem('lu_voice_debug', '1').
  private _dbgLines: string[] = [];
  private _voiceDebug = false;

  constructor() {
    try { this._voiceDebug = localStorage.getItem('lu_voice_debug') === '1'; } catch { /* Privatmodus */ }
    // Sprachwechsel: früher setzte applyTexts() den Hinweistext (data-i18n)
    // zurück auf «Ich höre zu» — den dynamischen Hinweis hier genauso leeren,
    // damit keine alte Fehlermeldung in der falschen Sprache stehen bleibt.
    events.on(EVENTS.LANGUAGE_CHANGED, () => { this.hint = null; this.emit(); });
  }

  vdbg(msg: string): void {
    console.log('[voice]', msg);
    if (!this._voiceDebug) return;
    this._dbgLines.push(msg);
    if (this._dbgLines.length > 4) this._dbgLines.shift();
    this.hint = this._dbgLines.join(' | ');
    this.emit();
  }

  setHint(text: string | null): void {
    this.hint = text;
    this.emit();
  }

  // ── Chat-Nachrichten (früher DOM-Appends in #ab-chat) ──────
  addMsg(text: string, who: ChatWho): MsgHandle {
    const m: TextMessage = { id: ++this.msgSeq, kind: 'text', who, text };
    this.messages.push(m);
    this.chatScrollTick += 1;
    this.emit();
    return {
      setText: (next: string) => { m.text = next; this.emit(); },
      remove: () => { this.messages = this.messages.filter((x) => x !== m); this.emit(); },
    };
  }

  addOfferCard(d: { offer_id?: string; offer_number?: string }): void {
    const num = d.offer_number || '';
    const oid = d.offer_id || '';
    this.messages.push({ id: ++this.msgSeq, kind: 'offer', offerId: oid, offerNumber: num });
    // (Der kurzlebige Preview-Token wird jetzt von der OfferCard-Komponente
    // selbst geholt — gleicher Endpunkt, gleicher Ablauf; nur der Ort ist
    // durch die Island-Architektur gewandert.)
    // agar dar halat-e seda hastim (chat makhfi) -> chat ra neshan bede ta factor dide shavad
    this.panelView = 'chat';
    this.chatScrollTick += 1;
    this.emit();
  }

  scrollChat(): void {
    this.chatScrollTick += 1;
    this.emit();
  }

  // Zeitstempel beim ENTSTEHEN der Nachricht setzen, nicht erst beim Speichern —
  // sonst trägt eine ganze Sitzung dieselbe Uhrzeit.
  histPush(entry: HistEntry): HistEntry {
    const e = entry && entry.ts ? entry : { ...entry, ts: Date.now() };
    this.chatHistory.push(e);
    return e;
  }

  // Werkzeug-Aufruf im Verlauf festhalten — ausdrücklich AUCH Fehlschläge:
  // genau die zeigen, wo der Assistent den Nutzer nicht verstanden hat.
  // Das ist eine INTERNE Spur für die Auswertung: im Chat wird sie NIE gezeigt.
  // Der Nutzer sieht das Ergebnis seiner Bitte, nicht die Maschinerie dahinter.
  // Darum auch neutraler Text statt t() — er hängt nicht von der UI-Sprache ab.
  recordToolCall(name: string, args: unknown, result: any): void {
    const r = (result && result.result) || '';
    const ok = r !== 'error' && r !== 'permission_denied' && r !== 'ui_unavailable';
    let argTxt = '';
    try {
      if (args && typeof args === 'object') {
        argTxt = Object.keys(args).slice(0, 4)
          .map((k) => k + '=' + String((args as Record<string, unknown>)[k]).slice(0, 40))
          .join(', ');
      }
    } catch { /* Argumente sind optional */ }
    const text = name + (argTxt ? ' (' + argTxt + ')' : '') + (ok ? '' : ' — ' + (r || 'failed'));
    this.histPush({ role: 'tool', text, name, ok });
  }

  flushVoiceText(role: 'user' | 'model'): boolean {
    const isUser = role === 'user';
    const text = ((isUser ? this.vtUserBuf : this.vtModelBuf) || '').trim();
    const lang = isUser ? this.vtUserLang : this.vtModelLang;
    if (isUser) { this.vtUserBuf = ''; this.vtUserLang = ''; } else { this.vtModelBuf = ''; this.vtModelLang = ''; }
    if (!text) return false;
    // Der Nutzer hat ausgesprochen → neue Runde. Ab hier zählt eine
    // Zustimmung zu einem vorher gezeigten Entwurf als echte Antwort.
    if (isUser) startTurn();
    this.histPush({ role: isUser ? 'user' : 'model', parts: [{ text }], lang: lang || undefined });
    return true;
  }
  flushVoiceAll(): boolean {
    const a = this.flushVoiceText('user');
    const b = this.flushVoiceText('model');
    return a || b;
  }

  // ── Persistenz ─────────────────────────────────────────────
  async saveChat(kind?: 'text' | 'voice'): Promise<void> {
    // faghat agar payami vojood darad
    if (!this.chatHistory.length) return;
    // matn-e har payam ra az parts darmiavarim
    const msgs = this.chatHistory.map((h) => {
      const ts = h.ts || Date.now();
      if (h.role === 'offer') {
        return { role: 'offer', text: h.offer_id || '', offer_id: h.offer_id || '', ts };
      }
      // 'tool' = welches Werkzeug der Assistent gerufen hat (nur Sprachpfad)
      if (h.role === 'tool') {
        return { role: 'tool', text: h.text || '', name: h.name || '', ok: h.ok !== false, ts };
      }
      const m: Record<string, unknown> = {
        role: h.role,
        text: (h.parts || []).map((p) => p.text).filter(Boolean).join(' '),
        ts,
      };
      if (h.lang) m.lang = h.lang;   // Sprache, die Gemini erkannt hat
      return m;
    }).filter((m) => m.text);
    if (!msgs.length) return;
    // Titel NUR aus Gesprochenem: Werkzeug-Notizen sind intern und dürfen auch
    // nicht als Titel in der Verlaufsliste auftauchen. Leer ⇒ Liste zeigt 'Chat'.
    const firstSpoken = msgs.find((m) => m.role === 'user' || m.role === 'model');
    const title = firstSpoken ? String(firstSpoken.text).slice(0, 80) : '';
    try {
      const res = await authedFetch(VOICE_HISTORY_BASE + '/save', {
        method: 'POST',
        body: JSON.stringify({ id: this.currentChatId, title, messages: msgs, kind: kind || this.currentChatKind }),
      });
      const data = await res.json();
      if (data.success && data.id) this.currentChatId = data.id;
    } catch { /* save khatā — sākhet nist */ }
  }

  async loadHistoryList(): Promise<HistoryChatMeta[]> {
    try {
      const res = await authedFetch(VOICE_HISTORY_BASE + '/list');
      const data = await res.json();
      return data.success ? (data.chats || []) : [];
    } catch { return []; }
  }

  async renderHistList(): Promise<void> {
    this.allChats = null; // Ladeanzeige «...»
    this.emit();
    this.allChats = await this.loadHistoryList();
    this.emit();
  }

  async deleteChat(id: string): Promise<void> {
    try {
      const res = await authedFetch(VOICE_HISTORY_BASE + '/' + id, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        this.allChats = (this.allChats || []).filter((c) => c.id !== id);
        // agar chat-e baz hamin bud, chat ra khali kon
        if (this.currentChatId === id) {
          this.messages = [];
          this.chatHistory = [];
          this.currentChatId = null;
        }
        this.emit();
      }
    } catch { /* ignore */ }
  }

  async openChat(id: string): Promise<void> {
    try {
      await this.saveChat();  // chat-e fe'li ra zakhire kon ghabl az baz kardan-e ghadimi
      const res = await authedFetch(VOICE_HISTORY_BASE + '/' + id);
      const data = await res.json();
      if (!data.success || !data.chat) return;
      // pak kardan-e chat-e fe'li va chidan-e payam‌ha
      this.messages = [];
      this.chatHistory = [];
      this.currentChatId = data.chat.id;
      this.currentChatKind = data.chat.kind === 'voice' ? 'voice' : 'text';
      const msgs: HistEntry[] = Array.isArray(data.chat.messages) ? data.chat.messages : [];
      for (const m of msgs) {
        if (m.role === 'offer') {
          this.addOfferCard({ offer_id: m.offer_id || m.text, offer_number: '' });
          this.histPush({ role: 'offer', offer_id: m.offer_id || m.text, ts: m.ts });
          continue;
        }
        // Werkzeug-Notizen sind eine INTERNE Spur (Auswertung) — der Nutzer sieht
        // sie nicht. Trotzdem in chatHistory behalten, sonst löscht das nächste
        // saveChat dieser Sitzung sie aus der Datenbank.
        if (m.role === 'tool') {
          this.histPush({ role: 'tool', text: m.text, name: m.name, ok: m.ok !== false, ts: m.ts });
          continue;
        }
        this.addMsg(m.text || '', m.role === 'user' ? 'user' : 'ai');
        this.histPush({ role: m.role, parts: [{ text: m.text }], ts: m.ts, lang: m.lang });
      }
      // panel az qabl baz ast — faqat nama-ye chat ra neshan bede (bedun refresh-e list)
      if (!this.panelOpen) {
        this.openPanel('text');
      } else {
        this.panelView = 'chat';
      }
      this.histCollapsed = true; // sidebar ra bebabd ta chat dide shavad
      this.emit();
    } catch (e) { console.error('[assistant] openChat', e); }
  }

  async newChat(): Promise<void> {
    await this.saveChat();       // chat-e fe'li ra zakhire kon (sabr kon ta tamam shavad)
    this.messages = [];
    this.chatHistory = [];
    this.currentChatId = null;   // baad az save -> id null ta chat-e jadid sakhte shavad
    this.currentChatKind = 'text';
    this.lastOfferId = null;
    this.histCollapsed = true;  // sidebar ra bebabd
    // nama-ye chat (bedun refresh-e kamel-e panel)
    this.panelView = 'chat';
    this.emit();
    if (this.host) this.host.focusInput();
  }

  setHistCollapsed(v: boolean): void {
    this.histCollapsed = v;
    this.emit();
  }
  toggleHist(): void {
    this.histCollapsed = !this.histCollapsed;
    if (!this.histCollapsed) this.renderHistList(); // baz shod -> list ra taze kon
    else this.emit();
  }

  // ── panel helpers ──
  openPanel(mode: 'text' | 'voice'): void {
    this.panelOpen = true;
    // (Body-Scroll-Lock + visualViewport-Ghofl übernimmt die React-Insel —
    // useDockViewportLock — abhängig von panelOpen; Verhalten unverändert.)
    this.renderHistList(); // har bar baz shod, list ra taze kon
    if (mode === 'text') {
      this.panelView = 'chat';
      if (this.host) this.host.focusInput();
    } else {
      this.panelView = 'voice';
    }
    this.emit();
  }

  closePanel(): void {
    this.saveChat();  // ghabl az bastan zakhire kon
    this.panelOpen = false;
    this.emit();
    stopVoice(this);
  }

  // ── Navigations-Kommandos: Ziel-Screen auch SEHEN ──────────
  // Nach einem erfolgreich dispatchten OPEN_* verdeckt das Panel (mobil:
  // Vollbild) genau den Screen, den der Nutzer sehen wollte — «erstell mir
  // eine Offerte» endete sonst auf dem Assistenten-Chat statt auf der Offerte.
  hidePanelForNavigation(): void {
    if (!this.panelOpen) return; // headless/orb: nichts zu tun
    this.saveChat();
    this.panelOpen = false;
    this.emit();
    // BEWUSST kein stopVoice() (Unterschied zu closePanel): im Sprachmodus
    // läuft das Gespräch weiter, während der Nutzer den Screen ansieht.
  }

  // ── Sichtbarkeit während offener Formulare ─────────────────
  resumeVisualsAfterForm(): void {
    this.visualsPaused = false;
    this.emit();
    if (this.siri && !this.rafId) { try { tick(this); } catch { /* wie zuvor */ } }
  }
  pauseVisualsForForm(): void {
    this.visualsPaused = true;
    this.emit();
    if (this.rafId) { cancelAnimationFrame(this.rafId); this.rafId = null; }
  }

  // ── E-Mail-Formular (Voice-Pfad) ───────────────────────────
  hideEmailForm(): void {
    this.emailForm = null;
    this.micPaused = false; this.formOpen = false;
    this.resumeVisualsAfterForm();
  }

  showEmailForm(offerId: string | null): void {
    this.exitHeadlessForVisuals(); // Formular braucht UI — Panel doch öffnen
    this.micPaused = true;
    this.formOpen = true;
    try { stopPlayback(this); } catch { /* wie zuvor */ }
    this.pauseVisualsForForm();
    const seq = ++this.formSeq;
    this.emailForm = { offerId, visible: false, status: '', seq };
    this.emit();
    // ba ye takhir kootah neshan bede ta vasat-e peak-e pardazesh-e seda nabashe (lag nadeh)
    // (Fokus auf das Feld setzt die Overlay-Komponente 250 ms nach dem Sichtbarwerden.)
    setTimeout(() => {
      if (this.emailForm && this.emailForm.seq === seq) {
        this.emailForm.visible = true;
        this.emit();
      }
    }, 350);
  }

  async sendEmailNow(email: string): Promise<void> {
    const f = this.emailForm;
    if (!f) return;
    const setStatus = (s: string) => { if (this.emailForm) { this.emailForm.status = s; this.emit(); } };
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || '').trim());
    if (!emailOk) { setStatus(t('assistant.invalidEmail')); return; }
    setStatus(t('assistant.sending'));
    try {
      const res = await authedFetch(VOICE_EMAIL_URL, {
        method: 'POST',
        body: JSON.stringify({ offer_id: f.offerId, email: (email || '').trim() }),
      });
      if (res.status === 403) { setStatus(t('assistant.noSendPermission')); return; }
      const d = await res.json();
      if (d.success) {
        setStatus(t('assistant.sentTo', { email: d.sent_to || (email || '').trim() }));
        setTimeout(() => this.hideEmailForm(), 1500);
      } else {
        setStatus(t('assistant.error', { detail: d.error || t('assistant.unknown') }));
      }
    } catch {
      setStatus(t('assistant.connectionError'));
    }
  }

  // ── form-e moshtari (masir-e sooti: open_customer_form -> in form ba prefill baz mishavad) ──
  hideCustomerForm(): void {
    this.custForm = null;
    this.micPaused = false; this.formOpen = false;
    // mic ra dobare vasl kon (agar hanooz session/processor barpast)
    try { if (this.micSource && this.processor) this.micSource.connect(this.processor); } catch { /* wie zuvor */ }
    this.resumeVisualsAfterForm();
  }

  showCustomerForm(prefill: CustomerPrefill | null | undefined): void {
    this.exitHeadlessForVisuals(); // Formular braucht UI — Panel doch öffnen
    const p = prefill || {};
    this.micPaused = true;
    this.formOpen = true;
    // mic ra VAGHEAN ghat kon (na faghat flag) ta seda-ye mic javab-e model ra interrupt nakonad
    try { if (this.micSource && this.processor) this.micSource.disconnect(this.processor); } catch { /* wie zuvor */ }
    // stopPlayback-e avval ra hazf kardim ta harf-e model ("form ra baz mikonam") nimekare ghat nashavad
    this.pauseVisualsForForm();
    // form ra FOURAN baz kon (be kickoff vabaste nist)
    // (Feldwerte + Fokus nach 250 ms übernimmt die Overlay-Komponente aus prefill.)
    this.custForm = { prefill: p, status: '', seq: ++this.formSeq };
    this.emit();
    // dar masir-e sooti: be model bogu ke form baz shod (ba ta'khir ta ba seda-ye mic interrupt nashavad)
    if (this.session) {
      setTimeout(() => {
        if (!this.session) return;
        try {
          // ghabl az kickoff seda-ye baghimande ra pak kon ta model dar sokoot-e tamiz shoroo konad (mesl-e salam-e aval)
          try { stopPlayback(this); } catch { /* wie zuvor */ }
          const msg = 'Es wurde gerade ein vorausgefülltes Kundenformular auf dem Bildschirm geöffnet. Sage dem Nutzer jetzt kurz (in seiner Sprache), dass ein Formular geöffnet wurde, er die Angaben (Name und Details) prüfen und bei Bedarf korrigieren soll, und dann auf den Knopf Erstellen klicken soll. Sag nur diese eine kurze Aufforderung.';
          this.session.sendClientContent({ turns: [{ role: 'user', parts: [{ text: msg }] }], turnComplete: true });
          this.vdbg('form kickoff sent');
        } catch (e: any) { this.vdbg('form kickoff fail: ' + (e && e.message ? e.message : '')); }
      }, 1800);
    }
  }

  async createCustomerNow(fields: { name: string; phone: string; street: string; plz: string; city: string; email: string }): Promise<void> {
    if (!this.custForm) return;
    const setStatus = (s: string) => { if (this.custForm) { this.custForm.status = s; this.emit(); } };
    const name = (fields.name || '').trim();
    if (!name) { setStatus(t('assistant.nameRequired')); return; }
    const email = (fields.email || '').trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setStatus(t('assistant.invalidEmailShort')); return; }
    setStatus(t('assistant.creating'));
    try {
      const res = await authedFetch(VOICE_RUNTOOL_URL, {
        method: 'POST',
        body: JSON.stringify({ name: 'create_customer', args: {
          name,
          phone_number: (fields.phone || '').trim(),
          email,
          street: (fields.street || '').trim(),
          postal_code: (fields.plz || '').trim(),
          city: (fields.city || '').trim(),
        } }),
      });
      if (res.status === 403) { setStatus(t('common.noPermission')); return; }
      const d = await res.json();
      const r = d && d.result;
      if (d.success && r && (r.result === 'created' || r.result === 'already_exists')) {
        const made = r.result === 'created';
        setStatus(made ? t('assistant.customerCreated') : t('assistant.customerExists'));
        this.addMsg(t(made ? 'assistant.customerCreatedMsg' : 'assistant.customerExistsMsg', { name }), 'ai');
        setTimeout(() => this.hideCustomerForm(), 1200);
      } else {
        setStatus(t('assistant.error', { detail: (r && r.detail) || d.error || t('assistant.unknown') }));
      }
    } catch {
      setStatus(t('assistant.connectionError'));
    }
  }

  // ── Text-Pfad: E-Mail-Nachfrage im Chat (kein Formular) ────
  async sendCustomerEmail(email: string): Promise<void> {
    // in_hal_ersal_marker — yek payam misazim va sepas hamoon ra update mikonim
    const statusMsg = this.addMsg(t('assistant.emailSending', { email }), 'ai');
    let finalText = t('assistant.emailConnectionError');
    try {
      const res = await authedFetch(VOICE_EMAIL_URL, {
        method: 'POST',
        body: JSON.stringify({ offer_id: this.pendingEmailOfferId, email }),
      });
      if (res.status === 403) { statusMsg.setText(t('assistant.noSendPermission')); this.scrollChat(); return; }
      const d = await res.json();
      if (d.success) {
        finalText = t('assistant.emailSent', { email: d.sent_to || email });
      } else {
        finalText = t('assistant.emailSendError', { detail: d.error || t('assistant.unknown') });
      }
    } catch {
      finalText = t('assistant.emailConnectionError');
    }
    statusMsg.setText(finalText);
    this.scrollChat();
    this.awaitingEmail = false;
    this.pendingEmailOfferId = null;
    // vaziat-e ersal ra dar history zakhire kon ta baad az baz kardan ham dide shavad
    this.histPush({ role: 'model', parts: [{ text: finalText }] });
    this.saveChat();
  }

  // ── Text-Pfad: Nachricht senden (früher submitText) ────────
  // Der Rohtext kommt aus der React-Textarea; Leeren/auto-grow des Feldes
  // macht die Komponente. Logik ansonsten unverändert.
  async sendText(rawText: string): Promise<void> {
    // NICHT `t` nennen — das würde die i18n-Funktion t() shadowen und
    // alle t('…')-Aufrufe weiter unten (Fehlermeldungen!) lautlos brechen.
    const text = (rawText || '').trim();
    if (!text) return;
    // agar montazer-e email hastim, in matn ra be onvane email begir
    if (this.awaitingEmail) {
      this.openPanel('text');
      this.addMsg(text, 'user');
      const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text);
      if (!isEmail) { this.addMsg(t('assistant.invalidEmailRetry'), 'ai'); return; }
      await this.sendCustomerEmail(text);
      return;
    }
    this.openPanel('text');
    this.addMsg(text, 'user');
    this.histPush({ role: 'user', parts: [{ text }] });
    startTurn();

    const thinking = this.addMsg('...', 'ai');
    this.scrollChat();

    try {
      const res = await authedFetch(VOICE_CHAT_URL, {
        method: 'POST',
        // 'offer'/'tool' sind reine Verlaufs-Notizen — Gemini kennt diese Rollen nicht.
        body: JSON.stringify({ history: this.chatHistory.filter((h) => h.role !== 'offer' && h.role !== 'tool').map((h) => ({ role: h.role, parts: h.parts })), last_offer_id: this.lastOfferId, lang: getLanguage(), ui_context: { open_document_id: getOpenDocument(), voice_targets: collectVoiceTargets(), turn_key: getTurnKey(), send_sheet: getSendSheetOpen() } }),
      });
      const data = await res.json();
      thinking.remove();
      if (data.success) {
        const wantEmail = !!(data.open_email_form && data.email_offer_id);
        const emailSent = !!data.email_sent;
        // dar halat-e email, payam-e model ra neshan nemidim (ta tekrar nashavad ba payam-e dasti-ye ma)
        if (!wantEmail) {
          const reply = data.reply || '...';
          this.addMsg(reply, 'ai');
          // appended = masir-e kamel-e in nobat (functionCall/functionResponse/model parts) az backend.
          // ein-an negah midarim ta nobat-e baad model tool-haye ghabli ra ham bebinad (hafeze-ye tool).
          if (Array.isArray(data.appended) && data.appended.length) {
            for (const part of data.appended) this.histPush(part);
          } else {
            this.histPush({ role: 'model', parts: [{ text: reply }] });
          }
        }
        // agar offer-e jadid sakhte shode -> kart-e preview (iframe) neshan bede
        if (data.last_offer_id && data.last_offer_id !== this.lastOfferId) {
          this.addOfferCard({ offer_id: data.last_offer_id, offer_number: data.offer_number || '' });
          this.histPush({ role: 'offer', offer_id: data.last_offer_id });
        }
        if (data.last_offer_id) this.lastOfferId = data.last_offer_id;
        // agar model darkhast-e email dad -> dar chat-e matni montazer-e email mishavim (form faghat baraye voice)
        if (wantEmail) {
          this.awaitingEmail = true;
          this.pendingEmailOfferId = data.email_offer_id;
          const askMsg = t('assistant.enterCustomerEmail');
          this.addMsg(askMsg, 'ai');
          this.histPush({ role: 'model', parts: [{ text: askMsg }] });
        }
        // agar email mostaghim ersal shod -> vaziat ra neshan bede va zakhire kon
        if (emailSent) {
          const sentMsg = t('assistant.emailSent', { email: data.email_sent_to || '' });
          this.addMsg(sentMsg, 'ai');
          this.histPush({ role: 'model', parts: [{ text: sentMsg }] });
        }
        // UI-Navigation az masir-e matni: haman ui_command-haye voice —
        // backend jam' karde, inja rooye UI-Command-Bus dispatch mishavand.
        // Zwei Unterschiede zum Voice-Pfad, beide bewusst:
        // (1) NACH einem gelungenen Navigations-Befehl kurz warten, bevor der
        //     nächste Befehl kommt: SEND_SHEET wird vom Offer-Sheet des ZIEL-
        //     Dokuments behandelt, und das montiert sich erst im nächsten
        //     React-Zyklus. Ohne die Pause verpuffte «umwandeln und senden»
        //     an dieser Stelle still.
        // (2) Scheitert ein Navigations-Befehl, das dem Nutzer SAGEN. Die
        //     Antwort des Modells ist hier längst geschrieben («ist geöffnet»)
        //     — der Voice-Pfad meldet ui_unavailable an das Modell zurück, im
        //     Textpfad geht das nicht mehr. Stillschweigen hiesse: der
        //     Assistent behauptet einen offenen Bildschirm, der nie kam.
        if (Array.isArray(data.ui_commands)) {
          let navigated = false;
          let navFailed = false;
          for (const cmd of data.ui_commands) {
            try {
              const handled = dispatchUiCommand(cmd);
              if (handled && cmd && NAV_UI_COMMANDS.has(cmd.type)) {
                navigated = true;
                if (cmd.type === 'OPEN_OFFER' && cmd.documentId) setOpenDocument(String(cmd.documentId));
                await new Promise((r) => setTimeout(r, 150));
              } else if (!handled && cmd && NAV_UI_COMMANDS.has(cmd.type)) {
                navFailed = true;
              }
            } catch { /* sokoot */ }
          }
          if (navFailed) {
            const failMsg = t('assistant.screenNotOpened');
            this.addMsg(failMsg, 'ai');
            // auch ins Gedächtnis: sonst beharrt das Modell in der nächsten
            // Runde auf seiner Behauptung, der Bildschirm sei offen.
            this.histPush({ role: 'user', parts: [{ text: '[App] The requested screen could NOT be opened in the current view. Do not claim it is on screen.' }] });
          }
          if (navigated) this.hidePanelForNavigation();
        }
        if (Array.isArray(data.changed_document_ids)) {
          for (const id of data.changed_document_ids) {
            try { dispatchUiCommand({ type: 'DOCUMENT_CHANGED', documentId: String(id) }); } catch { /* sokoot */ }
          }
        }
        this.saveChat();  // baad az javab zakhire kon
      } else {
        this.addMsg(t('assistant.error', { detail: errDetail(data.error) || t('assistant.unknown') }), 'ai');
      }
    } catch (e: any) {
      thinking.remove();
      this.addMsg(t('assistant.connectionFailed', { detail: e.message }), 'ai');
    }
  }

  // ── Headless-Voice-Steuerung ───────────────────────────────
  notifyHeadless(state: HeadlessState): void {
    if (this.headless && this.onHeadlessState) { try { this.onHeadlessState(state); } catch { /* Callback der Mobile-UI */ } }
  }
  exitHeadlessForVisuals(): void {
    if (!this.headless) return;
    this.notifyHeadless('visual');
    if (this.onHeadlessActivity) { try { this.onHeadlessActivity(null); } catch { /* Callback der Mobile-UI */ } }
    this.headless = false; this.onHeadlessState = null; this.onHeadlessActivity = null;
    this.openPanel('voice');
  }

  setActivity(label: string | null): void {
    if (this.headless) {
      if (this.onHeadlessActivity) { try { this.onHeadlessActivity(label); } catch { /* Callback der Mobile-UI */ } }
      return;
    }
    // null ⇒ die UI zeigt wieder t('assistant.listening') (wie früher der
    // data-i18n-Standardtext des Hinweises).
    this.setHint(label || null);
  }

  // Läuft gerade eine Sprachsitzung (Session offen ODER noch im Aufbau)?
  // Ersetzt den alten Check `session || micBtn.classList.contains('ab-recording')`.
  isVoiceOn(): boolean {
    return !!this.session || this.micRecording;
  }

  startVoice(): void { startVoice(this); }
  stopVoice(): void { stopVoice(this); }

  // Headless-API (siehe startMobileVoice/stopMobileVoice in AssistantBar.js)
  startHeadless(onState?: (s: HeadlessState) => void, onActivity?: (label: string | null) => void): void {
    if (this.isVoiceOn()) return; // läuft schon
    this.headless = true;
    this.onHeadlessState = onState || null;
    this.onHeadlessActivity = onActivity || null;
    startVoice(this);
  }
}
